import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Receipt, Search, Loader2, FileText, Send, CheckCircle2 } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Billing() {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("pending");
  const [billDialog, setBillDialog] = useState(null);
  const [unitPrice, setUnitPrice] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [client, setClient] = useState("");
  const [clientNif, setClientNif] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [p, h, c] = await Promise.all([
        axios.get(`${API}/billing`),
        axios.get(`${API}/billing/history`),
        axios.get(`${API}/clients`),
      ]);
      setPending(p.data);
      setHistory(h.data);
      setClients(c.data);
    } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const openBill = (order) => {
    setBillDialog(order);
    setClient(order.client || "");
    setClientNif(order.client_nif || "");
    setClientAddress(order.client_address || "");
    setUnitPrice(order.unit_price || "");
    setInvoiceNumber(order.purchase_order_number || "");
  };

  const handleBill = async () => {
    if (!unitPrice || parseFloat(unitPrice) <= 0) { toast.error("Introduza o preço unitário"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/billing/${billDialog.id}/bill`, {
        unit_price: parseFloat(unitPrice),
        invoice_number: invoiceNumber,
        client: client,
        client_nif: clientNif,
        client_address: clientAddress,
      });
      toast.success(`Encomenda ${billDialog.code} faturada! Email enviado.`);
      setBillDialog(null);
      fetchData();
    } catch (e) { showError(e, "Erro ao faturar"); }
    finally { setSubmitting(false); }
  };

  const handleExportPdf = (o) => {
    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/logo-ep3d.png`;
    const total = (o.unit_price || 0) * (o.current_good_quantity || 0);
    const html = `<!DOCTYPE html><html><head><title>Faturação ${o.code}</title>
    <style>@page{size:A4;margin:15mm} body{font-family:Arial,sans-serif;color:#1e293b;font-size:11px}
    .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #f59e0b;padding-bottom:10px;margin-bottom:20px}
    .header img{height:50px} h1{font-size:18px;color:#0f172a}
    table{width:100%;border-collapse:collapse;margin:15px 0} th{background:#0f172a;color:#f59e0b;padding:8px 12px;text-align:left}
    td{padding:6px 12px;border-bottom:1px solid #e5e7eb}
    .total{font-size:20px;font-weight:bold;text-align:right;margin:20px 0;color:#0f172a}
    .footer{margin-top:30px;border-top:1px solid #e5e7eb;padding-top:10px;color:#9ca3af;font-size:9px;text-align:center}
    </style></head><body>
    <div class="header"><img src="${logoUrl}" alt="EP3D" onerror="this.outerHTML='<b>EP3D Produção</b>'" /><div style="text-align:right;font-size:10px"><strong>FATURAÇÃO</strong><br>${o.code}</div></div>
    <h1>Facturar encomenda nº ${o.code}</h1>
    <table>
    <tr><th>Campo</th><th>Valor</th></tr>
    <tr><td>Ordem</td><td><strong>${o.code}</strong></td></tr>
    <tr><td>Cliente</td><td><strong>${o.client || "-"}</strong></td></tr>
    <tr><td>Nº Fatura</td><td>${o.invoice_number || "-"}</td></tr>
    <tr><td>Peça</td><td>${o.part_code || ""} — ${o.part_description || ""}</td></tr>
    <tr><td>Quantidade Produzida</td><td>${(o.current_good_quantity || 0).toLocaleString()} peças</td></tr>
    <tr><td>Preço Unitário</td><td>${(o.unit_price || 0).toFixed(4)} EUR</td></tr>
    <tr><td>Valor Total</td><td><strong>${total.toFixed(2)} EUR</strong></td></tr>
    <tr><td>Data Faturação</td><td>${o.billed_at ? new Date(o.billed_at).toLocaleString("pt-PT") : "-"}</td></tr>
    <tr><td>Faturado por</td><td>${o.billed_by || "-"}</td></tr>
    </table>
    <p class="total">Total: ${total.toFixed(2)} EUR</p>
    <div class="footer">EP3D Produção — Faturação ${o.code} — ${new Date().toLocaleString("pt-PT")}</div>
    </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `Faturacao_${o.code}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const items = tab === "pending" ? pending : history;
  const filtered = items.filter(o =>
    (o.code || "").toLowerCase().includes(search.toLowerCase()) ||
    (o.part_code || "").toLowerCase().includes(search.toLowerCase()) ||
    (o.client || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPending = pending.length;
  const totalBilled = history.length;
  const totalValue = history.reduce((s, o) => s + (o.total_value || 0), 0);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-chivo text-3xl font-bold text-foreground flex items-center gap-3">
          <Receipt className="h-7 w-7" /> Faturação
        </h1>
        <p className="text-muted-foreground mt-1">Gestão de faturação de encomendas finalizadas</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-amber-600">Pendentes</p><p className="text-3xl font-bold text-amber-600">{totalPending}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-green-600">Faturadas</p><p className="text-3xl font-bold text-green-600">{totalBilled}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-blue-600">Valor Total</p><p className="text-3xl font-bold text-blue-600">{totalValue.toFixed(2)} EUR</p></CardContent></Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-1">
        <button onClick={() => setTab("pending")} className={`px-4 py-2 text-sm font-medium rounded-t-lg ${tab === "pending" ? "bg-amber-500 text-white" : "text-muted-foreground hover:bg-secondary"}`}>
          Pendentes ({totalPending})
        </button>
        <button onClick={() => setTab("history")} className={`px-4 py-2 text-sm font-medium rounded-t-lg ${tab === "history" ? "bg-green-500 text-white" : "text-muted-foreground hover:bg-secondary"}`}>
          Faturadas ({totalBilled})
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Nº Encomenda</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Peça</TableHead>
              <TableHead className="text-right">Quantidade</TableHead>
              {tab === "history" && <TableHead className="text-right">Preço Unit.</TableHead>}
              {tab === "history" && <TableHead className="text-right">Total</TableHead>}
              <TableHead>Data</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={tab === "history" ? 10 : 8} className="text-center py-8 text-muted-foreground">
                  {tab === "pending" ? "Sem encomendas pendentes de faturação" : "Sem histórico de faturação"}
                </TableCell></TableRow>
              ) : filtered.map(o => (
                <TableRow key={o.id}>
                  <TableCell className="font-mono font-bold">{o.code}</TableCell>
                  <TableCell className="font-mono text-amber-600">{o.purchase_order_number || o.invoice_number || "-"}</TableCell>
                  <TableCell className="font-medium">{o.client || "-"}</TableCell>
                  <TableCell>{o.part_code} <span className="text-xs text-muted-foreground">{o.part_description}</span></TableCell>
                  <TableCell className="text-right font-mono font-bold">{(o.current_good_quantity || 0).toLocaleString()}</TableCell>
                  {tab === "history" && <TableCell className="text-right font-mono">{(o.unit_price || 0).toFixed(4)} EUR</TableCell>}
                  {tab === "history" && <TableCell className="text-right font-mono font-bold text-green-600">{(o.total_value || 0).toFixed(2)} EUR</TableCell>}
                  <TableCell className="text-sm">{
                    tab === "pending" 
                      ? (o.completed_at ? new Date(o.completed_at).toLocaleDateString("pt-PT") : "-")
                      : (o.billed_at ? new Date(o.billed_at).toLocaleDateString("pt-PT") : "-")
                  }</TableCell>
                  <TableCell>
                    {tab === "pending" 
                      ? <Badge className="bg-amber-500 text-white">Pendente</Badge>
                      : <Badge className="bg-green-500 text-white">Faturada</Badge>
                    }
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {tab === "pending" ? (
                        <Button size="sm" className="gap-1 bg-green-600 hover:bg-green-700" onClick={() => openBill(o)}>
                          <Send className="h-3 w-3" /> Faturar
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500" onClick={() => handleExportPdf(o)} title="PDF">
                          <FileText className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bill Dialog */}
      <Dialog open={!!billDialog} onOpenChange={() => setBillDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-green-600" /> Faturar {billDialog?.code}</DialogTitle></DialogHeader>
          {billDialog && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><strong>Peça:</strong> {billDialog.part_code} — {billDialog.part_description}</p>
                <p><strong>Quantidade:</strong> {(billDialog.current_good_quantity || 0).toLocaleString()} peças</p>
              </div>
              <div className="space-y-2">
                <Label>Cliente</Label>
                <select
                  value={clients.find(c => c.name === client)?.id || ""}
                  onChange={e => {
                    const sel = clients.find(c => c.id === e.target.value);
                    if (sel) {
                      setClient(sel.name);
                      setClientNif(sel.nif || "");
                      setClientAddress([sel.address, sel.city, sel.postal_code].filter(Boolean).join(", "));
                    }
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">-- Selecionar da base de dados --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.nif ? ` (${c.nif})` : ""}</option>
                  ))}
                </select>
                <Input value={client} onChange={e => setClient(e.target.value)} placeholder="Ou escreva o nome manualmente" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>NIF / NIPC</Label>
                  <Input value={clientNif} onChange={e => setClientNif(e.target.value)} placeholder="Ex: 509123456" className="font-mono" />
                </div>
                <div className="space-y-2">
                  <Label>Nº Encomenda</Label>
                  <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="Ex: EC-2026/001" className="font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Morada</Label>
                <Input value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder="Morada do cliente" />
              </div>
              <div className="space-y-2">
                <Label>Preço Unitário (EUR) *</Label>
                <Input type="number" step="0.0001" min="0" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} placeholder="Ex: 0.0250" className="font-mono" />
              </div>
              {unitPrice > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-sm text-green-600">Valor Total</p>
                  <p className="text-2xl font-bold text-green-700">{(parseFloat(unitPrice) * (billDialog.current_good_quantity || 0)).toFixed(2)} EUR</p>
                </div>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <p className="font-bold">Email automático para contabilidade@engenharia3d.pt</p>
                <p>Será enviado com os dados da encomenda e PDF em anexo.</p>
              </div>
              <Button className="w-full bg-green-600 hover:bg-green-700 gap-2" onClick={handleBill} disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Faturar e Enviar Email
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
